import { useTranslation } from 'react-i18next';
import { LAYERS, QUICK_LAYERS } from '../../utils/zstack/engine';

/*
 * A Z-stack's panels (docs/FIELD_AI_VISION_SPEC.md 10.3), the same in every
 * camera; `zstack` is useZStack()'s answer, `tone` 'dark' over a viewfinder.
 *
 *   ZStackSettings  in the capture modes, when Z-stack is chosen: focus
 *                   stepping or by hand (stepping only where the camera
 *                   offers a focus distance), the size (a quick stack of 3,
 *                   4 or 5 layers, or the full one), and for a full stack
 *                   stepped by focus the layers (3 to 25) and the range.
 *   ZStackRun       while it is taken: "Layer 4 of 9", or the sweep's time
 *                   and frames with Stop; Cancel. A sweep that took over
 *                   from stepping says why, and how to sweep.
 *   ZStackReview    the kept layers as a strip, the cover starred, what was
 *                   dropped; Keep or Discard. A stack that failed says why.
 *
 * ZStackPanel shows whichever of the last two applies, or nothing.
 */
const palette = dark => ({
  text: dark ? '#fff' : 'var(--c-text-fg)',
  muted: { margin: 0, fontSize: 12, color: dark ? 'rgba(255,255,255,0.8)' : 'var(--c-text-muted-fg)' },
  border: dark ? 'rgba(255,255,255,0.5)' : 'var(--c-border-strong)',
});
const button = (dark, primary = false) => ({
  minHeight: 36, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: primary ? 700 : 400,
  border: `1px solid ${primary ? 'var(--c-primary)' : palette(dark).border}`,
  background: primary ? 'var(--c-primary)' : 'transparent', color: primary ? 'var(--c-on-accent)' : palette(dark).text,
});
const choice = (dark, active) => ({
  ...button(dark), minHeight: 32, padding: '4px 10px', fontWeight: active ? 700 : 400,
  border: `1px solid ${active ? 'var(--c-primary)' : palette(dark).border}`,
  background: active ? (dark ? 'rgba(255,255,255,0.25)' : 'var(--c-primary-soft)') : 'transparent',
});

export function ZStackSettings({ zstack, tone = 'light', testIdPrefix = 'camera' }) {
  const { t } = useTranslation('camera');
  const dark = tone === 'dark';
  const id = name => `${testIdPrefix}-zstack-${name}`;
  const { settings, path } = zstack;
  const quick = settings.quick || null;
  return (
    <div data-testid={id('settings')} data-path={path} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {zstack.canStep ? (
        <div role="radiogroup" aria-label={t('zstack.path')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['auto', 'manual'].map(p => (
            <button key={p} type="button" role="radio" aria-checked={path === p} data-testid={id(`path-${p}`)}
                    disabled={zstack.busy} onClick={() => zstack.setSettings({ path: p })} style={choice(dark, path === p)}>
              {t(`zstack.${p}`)}
            </button>
          ))}
        </div>
      ) : (
        <p style={palette(dark).muted} data-testid={id('manual-only')} data-blocked={zstack.stepBlocked || undefined}>
          {t(zstack.stepBlocked ? 'zstack.stepUnavailable' : 'zstack.manualOnly')}
        </p>
      )}
      <div role="radiogroup" aria-label={t('zstack.quick')} data-testid={id('quick')} data-quick={quick || undefined}
           style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13 }}>{t('zstack.quick')}</span>
        <button type="button" role="radio" aria-checked={!quick} data-testid={id('quick-off')}
                disabled={zstack.busy} onClick={() => zstack.setQuick(null)} style={choice(dark, !quick)}>
          {t('zstack.full')}
        </button>
        {QUICK_LAYERS.map(n => (
          <button key={n} type="button" role="radio" aria-checked={quick === n} data-testid={id(`quick-${n}`)}
                  aria-label={t('zstack.quickLayers', { n })} disabled={zstack.busy}
                  onClick={() => zstack.setQuick(n)} style={choice(dark, quick === n)}>
            <bdi dir="ltr">{n}</bdi>
          </button>
        ))}
      </div>
      {path === 'auto' && !quick && (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 }}>
            <span>{t('zstack.layers')} <bdi dir="ltr" data-testid={id('layers-value')}>{settings.layers}</bdi></span>
            <input type="range" min={LAYERS.min} max={LAYERS.max} step={1} value={settings.layers}
                   data-testid={id('layers')} aria-label={t('zstack.layers')} disabled={zstack.busy}
                   onChange={e => zstack.setSettings({ layers: Number(e.target.value) })} />
          </label>
          <div role="radiogroup" aria-label={t('zstack.span')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['whole', 'around'].map(s => (
              <button key={s} type="button" role="radio" aria-checked={settings.span === s} data-testid={id(`span-${s}`)}
                      disabled={zstack.busy} onClick={() => zstack.setSettings({ span: s })}
                      style={choice(dark, settings.span === s)}>
                {t(`zstack.${s}`)}
              </button>
            ))}
          </div>
        </>
      )}
      {quick
        ? <p style={palette(dark).muted} data-testid={id('quick-help')}>{t(path === 'auto' ? 'zstack.quickAutoHelp' : 'zstack.quickManualHelp', { n: quick })}</p>
        : <p style={palette(dark).muted}>{t(path === 'auto' ? 'zstack.autoHelp' : 'zstack.manualHelp', { count: settings.layers })}</p>}
    </div>
  );
}

export function ZStackRun({ zstack, tone = 'light', testIdPrefix = 'camera' }) {
  const { t } = useTranslation('camera');
  const dark = tone === 'dark';
  const { run } = zstack;
  const id = name => `${testIdPrefix}-zstack-${name}`;
  let text = t('zstack.selecting');
  if (run.phase === 'stepping') text = t('zstack.stepping', { done: run.done, total: run.total });
  if (run.phase === 'sweeping') text = t('zstack.sweeping', { s: Math.floor((run.ms || 0) / 1000), count: run.frames || 0 });
  return (
    <div data-testid={id('run')} data-phase={run.phase} aria-busy="true"
         style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', color: palette(dark).text }}>
      {run.fallback && (
        // Stepping did not work here: the stack is going on by hand, and says how.
        <p role="alert" data-testid={id('fallback')} data-reason={run.fallback}
           style={{ ...palette(dark).muted, flexBasis: '100%', fontSize: 13, color: palette(dark).text }}>
          <strong>{t(`zstack.fallback.${run.fallback}`)}</strong> {t('zstack.fallback.howTo')}
        </p>
      )}
      <span role="status" style={{ fontSize: 14, flex: 1, minWidth: 0 }}>{text}</span>
      {run.phase === 'sweeping' && (
        <button type="button" onClick={zstack.stop} data-testid={id('stop')} style={button(dark, true)}>
          {t('zstack.stop')}
        </button>
      )}
      {run.phase !== 'selecting' && (
        <button type="button" onClick={zstack.cancel} data-testid={id('cancel')} style={button(dark)}>
          {t('zstack.cancel')}
        </button>
      )}
    </div>
  );
}

export function ZStackReview({ zstack, onKeep, busy = false, tone = 'light', testIdPrefix = 'camera' }) {
  const { t } = useTranslation('camera');
  const dark = tone === 'dark';
  const { run } = zstack;
  const id = name => `${testIdPrefix}-zstack-${name}`;
  if (run.phase === 'failed') {
    return (
      <div data-testid={id('failed')} data-reason={run.reason} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p role="alert" style={{ ...palette(dark).muted, color: dark ? '#ffcdd2' : 'var(--c-danger-fg)' }}>
          {t(`zstack.failed.${run.reason}`)}
        </p>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={zstack.start} data-testid={id('again')} style={button(dark, true)}>
            {t('zstack.tryAgain')}
          </button>
          <button type="button" onClick={zstack.discard} data-testid={id('close')} style={button(dark)}>
            {t('zstack.close')}
          </button>
        </div>
      </div>
    );
  }
  const { stack } = run;
  const { rejected } = stack;
  return (
    <section aria-label={t('zstack.review', { count: stack.layers.length })} data-testid={id('review')}
             data-layers={stack.layers.length} style={{ display: 'flex', flexDirection: 'column', gap: 6, color: palette(dark).text }}>
      <strong style={{ fontSize: 14 }}>{t('zstack.review', { count: stack.layers.length })}</strong>
      <ol data-gesture="off" style={{ display: 'flex', gap: 6, overflowX: 'auto', listStyle: 'none', margin: 0, padding: 0 }}>
        {stack.layers.map((layer, i) => (
          <li key={layer.url || i} data-testid={id(`layer-${i}`)} data-cover={i === stack.cover}
              style={{ flex: '0 0 auto', textAlign: 'center', fontSize: 11 }}>
            {layer.url && (
              <img src={layer.url} alt={t('zstack.layer', { n: i + 1 })}
                   style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4,
                            outline: i === stack.cover ? '2px solid var(--c-primary)' : 'none' }} />
            )}
            <div>{i === stack.cover ? '★ ' : ''}<bdi dir="ltr">{Math.round(layer.sharpness)}</bdi></div>
          </li>
        ))}
      </ol>
      <p style={palette(dark).muted} data-testid={id('dropped')}>
        {t('zstack.dropped', { blurred: rejected.blurred, weak: rejected.weak, duplicate: rejected.duplicate,
                               over: rejected.over_cap, max: stack.settings.max_layers })}
      </p>
      <p style={palette(dark).muted}>{t('zstack.reviewHelp')}</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} onClick={() => onKeep?.(zstack.keep())} data-testid={id('keep')}
                style={button(dark, true)}>
          {t('zstack.keep', { count: stack.layers.length })}
        </button>
        <button type="button" disabled={busy} onClick={zstack.discard} data-testid={id('discard')} style={button(dark)}>
          {t('zstack.discard')}
        </button>
      </div>
    </section>
  );
}

export default function ZStackPanel({ zstack, onKeep, busy = false, tone = 'light', testIdPrefix = 'camera' }) {
  const { phase } = zstack.run;
  if (phase === 'stepping' || phase === 'sweeping' || phase === 'selecting') {
    return <ZStackRun zstack={zstack} tone={tone} testIdPrefix={testIdPrefix} />;
  }
  if (phase === 'review' || phase === 'failed') {
    return <ZStackReview zstack={zstack} onKeep={onKeep} busy={busy} tone={tone} testIdPrefix={testIdPrefix} />;
  }
  return null;
}
